import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Landmark, Plus, Trash2, CheckCircle2, XCircle, Clock, Ban, Calendar, AlertTriangle } from "lucide-react";

const KIND = { received: "شيك مستلم من عميل", paid: "شيك مدفوع لمورد" };
const STATUS = {
  pending: { label: "قيد الانتظار", cls: "bg-amber-100 text-amber-800", Icon: Clock },
  cleared: { label: "تم صرفه", cls: "bg-emerald-100 text-emerald-800", Icon: CheckCircle2 },
  bounced: { label: "مرتجع", cls: "bg-red-100 text-red-800", Icon: XCircle },
  cancelled: { label: "ملغى", cls: "bg-slate-100 text-slate-700", Icon: Ban },
};

export default function Cheques() {
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("all");
  const [form, setForm] = useState({ kind: "received", party_id: "", party_name: "", bank_name: "", cheque_number: "", amount: 0, currency: "SYP", issue_date: "", due_date: "", note: "" });

  const load = async () => {
    try {
      const [c, cu, su] = await Promise.all([api.get("/cheques"), api.get("/customers"), api.get("/suppliers")]);
      setItems(c.data); setCustomers(cu.data); setSuppliers(su.data);
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm({ kind: "received", party_id: "", party_name: "", bank_name: "", cheque_number: "", amount: 0, currency: "SYP", issue_date: "", due_date: "", note: "" }); setOpen(true); };
  const save = async () => {
    if (!form.party_name.trim() || form.amount <= 0) return toast.error("الحقول الأساسية مطلوبة");
    try { await api.post("/cheques", form); toast.success("تم إضافة الشيك"); setOpen(false); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const setStatus = async (cid, status) => {
    if (!confirm(status === "cleared" ? "تأكيد صرف الشيك (سيؤثر على رصيد الطرف)؟" : "تغيير الحالة؟")) return;
    try { await api.post(`/cheques/${cid}/status`, { status }); toast.success("تم"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const del = async (id) => { if (!confirm("حذف؟")) return; try { await api.delete(`/cheques/${id}`); load(); } catch(e){ toast.error(formatErr(e)); } };

  const filtered = tab === "all" ? items : items.filter(x => tab === "due-soon" ? (x.due_date && new Date(x.due_date) - new Date() <= 7*86400000 && x.status === "pending") : x.status === tab);
  const partyList = form.kind === "received" ? customers : suppliers;

  return (
    <div className="space-y-4" data-testid="cheques-page">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="w-10 h-10 rounded-md bg-primary grid place-items-center text-primary-foreground"><Landmark className="w-5 h-5" /></div>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl md:text-3xl font-heading font-bold">الشيكات الآجلة</h1>
          <p className="text-sm text-muted-foreground">تتبع الشيكات المستلمة والمدفوعة + تواريخ الاستحقاق</p>
        </div>
        <Button onClick={openNew} className="gap-2" data-testid="new-cheque-btn"><Plus className="w-4 h-4" /> شيك جديد</Button>
      </div>

      <div className="flex gap-1 p-1 bg-muted rounded-lg w-fit flex-wrap">
        {[["all","الكل"],["pending","قيد الانتظار"],["due-soon","تنتهي خلال 7 أيام"],["cleared","تم صرفه"],["bounced","مرتجع"]].map(([k,l]) => (
          <button key={k} onClick={()=>setTab(k)} className={`px-3 py-1 rounded-md text-xs font-bold ${tab===k?"bg-primary text-primary-foreground":""}`} data-testid={`cheque-tab-${k}`}>{l}</button>
        ))}
      </div>

      <div className="border border-border rounded-md bg-card overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>النوع</TableHead><TableHead>الطرف</TableHead><TableHead>البنك</TableHead>
            <TableHead>رقم الشيك</TableHead><TableHead>المبلغ</TableHead>
            <TableHead>تاريخ الاستحقاق</TableHead><TableHead>الحالة</TableHead><TableHead>إجراءات</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {filtered.map(c => {
              const S = STATUS[c.status]; const overdue = c.due_date && new Date(c.due_date) < new Date() && c.status === "pending";
              return (
                <TableRow key={c.id} data-testid={`cheque-row-${c.id}`} className={overdue ? "bg-red-50" : ""}>
                  <TableCell><span className={`text-[10px] px-2 py-0.5 rounded font-bold ${c.kind==="received"?"bg-emerald-100 text-emerald-800":"bg-primary/10 text-primary"}`}>{KIND[c.kind]}</span></TableCell>
                  <TableCell className="font-bold">{c.party_name}</TableCell>
                  <TableCell>{c.bank_name || "—"}</TableCell>
                  <TableCell className="font-mono text-xs" dir="ltr">{c.cheque_number || "—"}</TableCell>
                  <TableCell className="font-bold">{c.amount.toLocaleString("ar-EG")} {c.currency}</TableCell>
                  <TableCell className="text-xs">
                    <span className={overdue ? "text-red-700 font-bold flex items-center gap-1" : ""}>
                      <Calendar className="w-3 h-3" />{c.due_date ? new Date(c.due_date).toLocaleDateString("ar-EG") : "—"}
                      {overdue && <AlertTriangle className="w-3 h-3" />}
                    </span>
                  </TableCell>
                  <TableCell><span className={`text-[10px] px-2 py-0.5 rounded font-bold inline-flex items-center gap-1 ${S?.cls}`}><S.Icon className="w-3 h-3" />{S?.label}</span></TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      {c.status === "pending" && (
                        <>
                          <Button size="sm" variant="outline" onClick={()=>setStatus(c.id,"cleared")} className="h-6 text-[10px] gap-1 text-emerald-700 border-emerald-400" data-testid={`clear-${c.id}`}><CheckCircle2 className="w-3 h-3" />صرف</Button>
                          <Button size="sm" variant="outline" onClick={()=>setStatus(c.id,"bounced")} className="h-6 text-[10px] text-red-700 border-red-400" data-testid={`bounce-${c.id}`}>مرتجع</Button>
                        </>
                      )}
                      <Button size="sm" variant="ghost" onClick={()=>del(c.id)} className="h-6 text-destructive" data-testid={`del-cheque-${c.id}`}><Trash2 className="w-3 h-3" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
            {filtered.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-6 text-muted-foreground">لا توجد شيكات</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="cheque-dialog">
          <DialogHeader><DialogTitle>شيك جديد</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>النوع</Label>
              <Select value={form.kind} onValueChange={(v)=>setForm({...form, kind:v, party_id:"", party_name:""})}>
                <SelectTrigger data-testid="cheque-kind-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="received">شيك مستلم من عميل</SelectItem>
                  <SelectItem value="paid">شيك مدفوع لمورد</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>الطرف ({form.kind === "received" ? "العميل" : "المورد"})</Label>
              <Select value={form.party_id} onValueChange={(v)=>{ const p = partyList.find(x=>x.id===v); setForm({...form, party_id:v, party_name:p?.name||""}); }}>
                <SelectTrigger data-testid="cheque-party-select"><SelectValue placeholder="اختر أو اترك فارغاً للطرف اليدوي" /></SelectTrigger>
                <SelectContent>{partyList.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
              {!form.party_id && <Input value={form.party_name} onChange={(e)=>setForm({...form, party_name:e.target.value})} placeholder="اسم الطرف (يدوي)" className="mt-2" data-testid="cheque-party-input" />}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>البنك</Label><Input value={form.bank_name} onChange={(e)=>setForm({...form, bank_name:e.target.value})} data-testid="cheque-bank-input" /></div>
              <div><Label>رقم الشيك</Label><Input value={form.cheque_number} onChange={(e)=>setForm({...form, cheque_number:e.target.value})} dir="ltr" className="font-mono" data-testid="cheque-num-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>المبلغ</Label><Input type="number" step="0.01" value={form.amount} onChange={(e)=>setForm({...form, amount:+e.target.value})} data-testid="cheque-amt-input" /></div>
              <div>
                <Label>العملة</Label>
                <Select value={form.currency} onValueChange={(v)=>setForm({...form, currency:v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{["SYP","USD","TRY","EUR"].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>تاريخ الإصدار</Label><Input type="date" value={form.issue_date} onChange={(e)=>setForm({...form, issue_date:e.target.value})} data-testid="cheque-issue-input" /></div>
              <div><Label>تاريخ الاستحقاق</Label><Input type="date" value={form.due_date} onChange={(e)=>setForm({...form, due_date:e.target.value})} data-testid="cheque-due-input" /></div>
            </div>
            <div><Label>ملاحظة</Label><Input value={form.note} onChange={(e)=>setForm({...form, note:e.target.value})} data-testid="cheque-note-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpen(false)}>إلغاء</Button>
            <Button onClick={save} data-testid="save-cheque-btn">حفظ</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
